import { Dispatch } from 'react';
import { RootState } from '~/store/config';
import api from '~/services';
import { UserList, DepartmentList } from '~/types/accountInfo';
import { _, dayjs } from '~/dependencies';
import { log } from '~/helpers';
import {
  ParticipantTypeEnum,
  GroupTypesEnum,
  ConversationFilterEnum,
} from '~/enums/conversations';
import {
  Conversation,
  ConversationFilters,
  ConversationListRequestResponse,
  ConversationRequestResponse,
  ConversationList,
} from '~/interfaces/conversations';
import {
  SetConversationListAction,
  SetConversationTotalSizeAction,
  SetConversationOpenSelectionAction,
  SetConversationIsLoadingAction,
  SetConversationHiddenBottomNavAction,
  SetResetConversationsListAction,
  ResetConversationsAction,
  SetConversationLastReadIndexAction,
  SetConversationPageAction,
  SetConversationFiltersAction,
  SetConversationBadgeCountAction,
  SetConversationChatAction,
} from '../reducers/conversation';
import * as actionTypes from './actionTypes';

const populateAssignedTo = (
  conversation: Conversation,
  userList: UserList,
  departmentList: DepartmentList
) => {
  if (
    !conversation ||
    typeof conversation !== 'object' ||
    !Array.isArray(userList) ||
    !Array.isArray(departmentList)
  ) {
    return conversation;
  }

  if (userList.length && conversation.assignedId) {
    const userInfo = userList.find(e => e.id === conversation.assignedId);
    return {
      ...conversation,
      assignedName: userInfo?.name,
    };
  }

  if (departmentList.length && conversation.assignedDepartmentId) {
    const departmentInfo = departmentList.find(
      e => e.id === conversation.assignedDepartmentId
    );
    return {
      ...conversation,
      assignedName: departmentInfo?.name,
    };
  }

  return conversation;
};

/**
 * Used to extract to sender name for the last message sent
 */
const getSenderName = (conversation: ConversationRequestResponse) => {
  if (conversation && conversation.messages.length) {
    const { submittedByUserId } = conversation.messages[0];
    const sender = conversation.participants.find(
      (e: { participantId: string }) =>
        e.participantId === String(submittedByUserId)
    );

    if (sender) {
      return (
        sender.displayName || sender.participantId || 'Unknown Calling User'
      );
    }
  }

  return '';
};

/**
 * Get the contact name for the conversation
 */
const getContactName = (conversation: ConversationRequestResponse) => {
  let contactName = 'Unknown Calling User';
  if (conversation && conversation.participants.length) {
    const contact = conversation.participants.find(
      (e: { participantType: string; isSystemUser: boolean }) =>
        e.participantType === ParticipantTypeEnum.PHONE_NUMBER &&
        !e.isSystemUser
    );
    if (contact) {
      contactName = contact.contact?.name || contact.displayName || contactName;
    }
  }

  return contactName;
};

/**
 * Get the contact number for the conversation
 */
const getContactNumber = (conversation: ConversationRequestResponse) => {
  let contactNumber = 'Unknown Calling Number';
  if (conversation && conversation.participants.length) {
    const contact = conversation.participants.find(
      (e: { participantType: string; isSystemUser: boolean }) =>
        e.participantType === ParticipantTypeEnum.PHONE_NUMBER &&
        !e.isSystemUser
    );
    if (contact) {
      contactNumber =
        contact.participantId || contact.displayName || contactNumber;
    }
  }

  return contactNumber;
};

export const calculateTTR = (conversation: Conversation, userTZ: string) => {
  if (conversation && conversation.ttr && userTZ) {
    // Convert the current TTR location to the user timezone.
    const ttrDateConvertedToUserTz = dayjs(conversation.ttr)
      .tz(userTZ)
      .format();

    const now = dayjs(new Date())
      .tz(userTZ)
      .format();

    const differenceInMinutes = dayjs(ttrDateConvertedToUserTz).diff(
      now,
      'minutes'
    );

    conversation.ttr = differenceInMinutes > 0 ? differenceInMinutes : 0;
    conversation.groupType =
      differenceInMinutes > 0 && differenceInMinutes < 60
        ? GroupTypesEnum.IMMEDIATE_ACTION
        : GroupTypesEnum.LATEST_MESSAGES;
  }

  return conversation;
};

/**
 * Calculate unread messages for current user
 */
export const calculateUnreadMessagesForUser = (
  conversation: Conversation,
  userId: number
) => {
  if (!conversation || !Array.isArray(conversation.participants)) {
    return conversation;
  }
  const isParticipant = conversation.participants.some(
    e => e.participantId === String(userId)
  );

  return {
    ...conversation,
    unread: isParticipant ? conversation.unread : 0,
  };
};

export const mapToConversation = (
  conversation: ConversationRequestResponse,
  userInfo: any
): Conversation => {
  // FIXME: Can become a funciton called defineMessageDisplayName
  const messagesParsed = _.map(conversation.messages, (message: any) => {
    const submittedByUserName = conversation.participants.find(
      (e: { participantId: string }) =>
        Number(e.participantId) === message.submittedByUserId
    )?.displayName;

    const currentUserName =
      userInfo.userIdCode &&
      userInfo.userIdCode === Number(message?.sender?.participantId)
        ? userInfo.user_name
        : '';

    const displayName =
      submittedByUserName || message?.sender?.displayName || currentUserName;

    return {
      ...message,
      message: message.content,
      senderAgentDisplayName: displayName,
      // Include senderId to be used to identify if the message was sent by current user
      senderId: conversation?.senderId,
    };
  });

  // @ts-ignore FIXME: Conversation type is getting overloaded with with API response
  return {
    ...conversation,
    messages: messagesParsed,
    unread: conversation.unreadCount,
    ttr: conversation.expiresAt ?? 0,
    groupType: GroupTypesEnum.LATEST_MESSAGES,
    senderName: getSenderName(conversation),
    contactName: getContactName(conversation),
    contactNumber: getContactNumber(conversation),
    assignedId: conversation.assignedToId,
    archived: false, // API is not returning this property
    selected: false,
  };
};

export const setIsLoadingConversation = (isLoading: boolean) => (
  dispatch: Dispatch<SetConversationIsLoadingAction>
) => {
  dispatch({
    type: actionTypes.CONVERSARTION_ISLOADING,
    payload: isLoading,
  });
};

export const setUnreadMessageCount = () => async (
  dispatch: Dispatch<SetConversationBadgeCountAction>
) => {
  try {
    const response = await api.conversation.get.getUnreadMessagesCount();

    if (response.data && 'unreadCount' in response.data) {
      dispatch({
        type: actionTypes.CONVERSARTION_SET_BADGE_COUNTER,
        payload: response.data.unreadCount,
      });
    }
  } catch (err) {
    log.error(`Failure to get unread messages count. Reason: ${err}`);
  }
};

let getAllConversationsAbortController: AbortController | null = null;

export const getAllConversations = () => async (
  dispatch: Dispatch<
    | SetConversationListAction
    | SetConversationTotalSizeAction
    | SetConversationIsLoadingAction
    | SetConversationBadgeCountAction
  >,
  getState: () => RootState
) => {
  if (getAllConversationsAbortController) {
    getAllConversationsAbortController.abort();
  }
  getAllConversationsAbortController = new AbortController();
  const { signal } = getAllConversationsAbortController;

  try {
    const { conversations, filters } = getState().conversation;
    const {
      users: userList,
      departments: departmentList,
    } = getState().accountInfo;
    const userInfo = getState().auth;
    const {
      is_active,
      page,
      perPage,
      type: filterType,
      channelType,
      search,
      unread,
      unassigned,
    } = filters;
    const { userTimeZone, userIdCode } = getState().auth;

    setIsLoadingConversation(true)(dispatch);
    // Update unread messages count once received a new message.
    setUnreadMessageCount()(dispatch);

    // FIXME: Create a conversation response interface e.g. ConversationResponse
    const conversationList = await api.conversation.get.getConversations({
      signal,
      params: {
        is_active,
        page,
        per_page: perPage,
        ...(channelType && { channel: channelType }),
        ...(search && search.length > 0 && { search }),
        ...(unread && { unread: 1 }),
        ...(unassigned && { unassigned: 1 }),
      },
    });

    const currentFilters = getState().conversation.filters;
    if (
      currentFilters.channelType !== channelType ||
      currentFilters.type !== filterType
    ) {
      return;
    }

    const totalItems: number = conversationList.data.total;

    let conversationsData: ConversationListRequestResponse =
      conversationList.data.data;

    /**
     * WA2-7058: Required because of the current API behavior.
     * Filter conversations whose last message channel type matches the selected filter.
     */
    conversationsData = channelType
      ? _.filter(
          conversationsData,
          (conversation: ConversationRequestResponse) =>
            _.get(conversation, 'channelType') === channelType
        )
      : conversationsData;

    let conversationsParsed: ConversationList = _.map(
      conversationsData,
      (conversation: ConversationRequestResponse) =>
        mapToConversation(conversation, userInfo)
    );

    // Calculate unread messages only for conversations that the user is part of
    conversationsParsed = _.map(conversationsParsed, (c: Conversation) =>
      calculateUnreadMessagesForUser(c, userIdCode)
    );

    // Calculate TTR
    conversationsParsed = _.map(conversationsParsed, (c: Conversation) =>
      calculateTTR(c, userTimeZone)
    );

    // Populate assigned to user or department name
    conversationsParsed = _.map(conversationsParsed, (c: Conversation) =>
      populateAssignedTo(c, userList, departmentList)
    );

    conversationsParsed =
      page && page > 1
        ? [...conversationsParsed, ..._.cloneDeep(conversations)]
        : conversationsParsed;

    conversationsParsed = _.orderBy(
      conversationsParsed,
      (c: Conversation) => c.lastMessageAt,
      ['desc']
    );

    // Handle filters
    if (filterType === ConversationFilterEnum.UNREAD) {
      conversationsParsed = _.filter(
        conversationsParsed,
        (c: Conversation) => c.unread > 0
      );
    }

    // The order of result values is determined by the order they occur in the array.
    conversationsParsed = _.uniqBy(conversationsParsed, 'id');

    dispatch({
      type: actionTypes.CONVERSARTION_SET_TOTAL_SIZE,
      payload: totalItems,
    });
    dispatch({
      type: actionTypes.CONVERSATION_SET_CONVERSATIONS_LIST,
      payload: conversationsParsed,
    });

    setIsLoadingConversation(false)(dispatch);
    // eslint-disable-next-line prettier/prettier
  } catch (err) {
    // @ts-ignore
    if (err?.code === 'ERR_CANCELED' || err?.name === 'AbortError') return;
    log.error(`Failure to get all conversations. Reason: ${err}`);
  } finally {
    if (!signal.aborted) {
      setIsLoadingConversation(false)(dispatch);
    }
  }
};

export const fetchConversationById = (
  id: string,
  enableLoading: boolean = true
) => async (
  dispatch: Dispatch<
    SetConversationChatAction | SetConversationIsLoadingAction
  >,
  getState: () => RootState
) => {
  try {
    const userInfo = getState().auth;

    setIsLoadingConversation(enableLoading)(dispatch);

    const conversation = await api.conversation.get.getConversationById(id);
    const conversationsParsed = mapToConversation(conversation.data, userInfo);

    dispatch({
      type: actionTypes.CONVERSATION_SET_ACTIVE_CHAT,
      payload: conversationsParsed,
    });
  } catch (err) {
    log.error(`Failure to get conversations id: ${id}. Reason: ${err}`);
  } finally {
    setIsLoadingConversation(false)(dispatch);
  }
};

export const selectAllConversations = (openSelection: boolean) => (
  dispatch: Dispatch<SetConversationListAction>,
  getState: () => RootState
) => {
  const { conversations } = getState().conversation;
  const conversationList = _.map(conversations, (e: Conversation) => {
    return { ...e, selected: openSelection };
  });

  dispatch({
    type: actionTypes.CONVERSATION_SET_CONVERSATIONS_LIST,
    payload: conversationList,
  });
};

export const openConversationSelection = (openSelection: boolean) => (
  dispatch: Dispatch<
    | SetConversationListAction
    | SetConversationOpenSelectionAction
    | SetConversationHiddenBottomNavAction
  >,
  getState: () => RootState
) => {
  const { conversations } = getState().conversation;
  const conversationList = _.map(conversations, (e: Conversation) => {
    return { ...e, selected: false };
  });

  dispatch({
    type: actionTypes.CONVERSATION_SET_CONVERSATIONS_LIST,
    payload: conversationList,
  });
  dispatch({
    type: actionTypes.CONVERSARTION_OPEN_SELECTION,
    payload: openSelection,
  });
  dispatch({
    type: actionTypes.CONVERSARTION_HIDDEN_BOTTOMNAV,
    payload: openSelection,
  });
};

export const setHiddenBottomNav = (open: boolean) => (
  dispatch: Dispatch<SetConversationHiddenBottomNavAction>
) => {
  dispatch({
    type: actionTypes.CONVERSARTION_HIDDEN_BOTTOMNAV,
    payload: open,
  });
};

export const updateConversation = (conversation: Conversation) => (
  dispatch: Dispatch<SetConversationListAction>,
  getState: () => RootState
) => {
  const { conversations } = getState().conversation;

  const conversationIndex = conversations.findIndex(
    e => e.id === conversation.id
  );

  if (conversationIndex !== -1) {
    // FIXME: This approach can lead to issues as it mutates the state directly, better to use map
    conversations[conversationIndex] = conversation;

    dispatch({
      type: actionTypes.CONVERSATION_SET_CONVERSATIONS_LIST,
      payload: [...conversations],
    });
  }
};

export const updateConversationAssignedAgent = (
  conversationId: string,
  agentNewInfo: Pick<
    Conversation,
    'assignedId' | 'assignedDepartmentId' | 'assignedName'
  >
) => (
  dispatch: Dispatch<SetConversationListAction>,
  getState: () => RootState
) => {
  const { conversations } = getState().conversation;

  const updatedConversations = conversations.map(item =>
    item.id === conversationId
      ? {
          ...item,
          ...agentNewInfo,
        }
      : item
  );

  dispatch({
    type: actionTypes.CONVERSATION_SET_CONVERSATIONS_LIST,
    payload: updatedConversations,
  });
};

export const markConversationAsRead = () => async (
  dispatch: Dispatch<
    | SetConversationListAction
    | SetConversationIsLoadingAction
    | SetConversationBadgeCountAction
    | SetConversationTotalSizeAction
  >,
  getState: () => RootState
) => {
  const { conversations, filters } = getState().conversation;
  const { type: filterType } = filters;

  setIsLoadingConversation(true)(dispatch);

  let conversationsUpdated: ConversationList = await Promise.all(
    _.map(conversations, async (conversation: Conversation) => {
      if (conversation.selected) {
        await api.conversation.patch.readStatus(conversation.id, {
          status: 'read',
        });
        conversation.unread = 0;
        conversation.selected = false;
      }

      return conversation;
    })
  );

  if (filterType === ConversationFilterEnum.UNREAD) {
    conversationsUpdated = _.filter(
      conversationsUpdated,
      (e: Conversation) => e.unread > 0
    );
  }

  // Update unread messages count once received a new message.
  setUnreadMessageCount()(dispatch);

  dispatch({
    type: actionTypes.CONVERSATION_SET_CONVERSATIONS_LIST,
    payload: [...conversationsUpdated],
  });

  setIsLoadingConversation(false)(dispatch);
};

export const archiveConversations = () => async (
  dispatch: Dispatch<
    | SetConversationListAction
    | SetConversationOpenSelectionAction
    | SetConversationIsLoadingAction
  >,
  getState: () => RootState
) => {
  const { conversations } = getState().conversation;

  setIsLoadingConversation(true)(dispatch);

  let conversationsUpdated: ConversationList = await Promise.all(
    _.map(conversations, async (conversation: Conversation) => {
      if (conversation.selected) {
        await api.conversation.patch.archive(conversation.id);
        conversation.archived = true;
        conversation.selected = false;
      }

      return conversation;
    })
  );

  conversationsUpdated = _.filter(
    conversationsUpdated,
    (e: Conversation) => !e.archived
  );

  dispatch({
    type: actionTypes.CONVERSATION_SET_CONVERSATIONS_LIST,
    payload: conversationsUpdated,
  });

  dispatch({
    type: actionTypes.CONVERSARTION_OPEN_SELECTION,
    payload: false,
  });

  setIsLoadingConversation(false)(dispatch);
};

export const setConversationSearch = (search: string | undefined) => async (
  dispatch: Dispatch<SetConversationFiltersAction>,
  getState: () => RootState
) => {
  const { filters } = getState().conversation;

  dispatch({
    type: actionTypes.CONVERSARTION_SET_FILTERS,
    payload: { ...filters, search, page: 1 },
  });
};

export const setConversationFilter = (
  searchFilter: ConversationFilters
) => async (
  dispatch: Dispatch<
    | SetConversationIsLoadingAction
    | SetResetConversationsListAction
    | SetConversationFiltersAction
  >,
  getState: () => RootState
) => {
  const { filters } = getState().conversation;
  const { perPage } = filters;

  const nextFilters: ConversationFilters = {
    ...filters,
    ...searchFilter,
    page: 1,
    perPage,
  };

  // Avoid search already selected filter
  if (
    filters.type !== nextFilters.type ||
    filters.channelType !== nextFilters.channelType
  ) {
    dispatch({
      type: actionTypes.CONVERSARTION_SET_FILTERS,
      payload: nextFilters,
    });
  }
};

export const setConversationPage = (page: number) => async (
  dispatch: Dispatch<SetConversationPageAction>
) => {
  dispatch({
    type: actionTypes.CONVERSARTION_SET_PAGE,
    payload: page,
  });
};
/**
 * Set the last read index for a conversation
 * @param index The last read index
 */
export const setConversationLastReadIndex = (index: number) => async (
  dispatch: Dispatch<SetConversationLastReadIndexAction>
) => {
  dispatch({
    type: actionTypes.CONVERSATION_SET_LAST_READ_INDEX,
    payload: index,
  });
};

export const filterUnreadConversation = () => async (
  dispatch: Dispatch<SetConversationListAction>,
  getState: () => RootState
) => {
  const { conversations } = getState().conversation;

  const unreadConversations: ConversationList = _.filter(
    conversations,
    (c: Conversation) => c.unread > 0
  );

  dispatch({
    type: actionTypes.CONVERSATION_SET_CONVERSATIONS_LIST,
    payload: unreadConversations,
  });
};

export const cancelConversationMessage = (
  conversationId: string,
  messageId: string
) => async (
  dispatch: Dispatch<
    | SetConversationListAction
    | SetConversationChatAction
    | SetConversationIsLoadingAction
  >,
  getState: () => RootState
) => {
  setIsLoadingConversation(true)(dispatch);

  await api.conversation.delete.cancelMessage(conversationId, messageId);

  fetchConversationById(conversationId, false)(dispatch, getState);

  setIsLoadingConversation(false)(dispatch);
};

export const resetConversationsList = () => async (
  dispatch: Dispatch<SetResetConversationsListAction>
) => {
  dispatch({
    type: actionTypes.CONVERSATION_RESET_CONVERSATIONS_LIST,
  });
};

export const resetConversations = () => async (
  dispatch: Dispatch<ResetConversationsAction>
) => {
  dispatch({
    type: actionTypes.CONVERSATION_RESET,
  });
};
